import {
  Board,
  boardHasBeenPaidFor,
  PhysicalDetails,
  physicalDetailsLoaded,
  PhysicalDetailsState,
  Shipment,
} from "../../../../reducers/uploadedBoard";
import FlashPCBSelect, { OptionType } from "../../../common/FlashPCBSelect";
import { useBoardFromContext } from "../../../../utils/hooks/commonly-stored/uploaded-board/board";
import React, { useCallback } from "react";
import { InputLabel } from "../../../../constants/Styles";
import { LineItemDescription } from "../../../common/LineItem/LineItemStyles";
import { SingleValue } from "react-select";
import { useFlashPCBSettings } from "../../../../utils/hooks/commonly-stored/flashPCBSettings";
import LoadingIcon from "../../../common/LoadingIcon";
import FlashPCBTooltip from "../../../common/FlashPCBTooltip";
import InfoIcon from "../../../common/FlashPCBTooltip/InfoIcon";
import {
  Container,
  DetailLine,
  Label,
  QuoteOptionContainer,
} from "../ProjectDetailsStyles";
import { ProcessingPill } from "../";
import { DEMO_BOARD_SLUG } from "../../../../constants/boards";
import {
  BoardTransitionEvent,
  MachineGroup,
} from "../../../../utils/hooks/commonly-stored/uploaded-board/state-machine/sendBoardEvent";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks/store";
import { useSendUpdateEvent } from "../../../../utils/hooks/commonly-stored/uploaded-board/updates/sendUpdates";
import { FlashPCBSettings } from "../../../../utils/settings";
import useShipmentsFromContext from "../../../../utils/hooks/commonly-stored/uploaded-board/shipments";

enum Layer {
  INNER = "inner",
  OUTER = "outer",
}

export const innerCopperWeightIsInstantQuoteValue = (
  settings: FlashPCBSettings,
  innerCopperWeight: number,
) =>
  settings.instant_quote_options.inner_copper_weight.includes(
    innerCopperWeight,
  );
export const outerCopperWeightIsInstantQuoteValue = (
  settings: FlashPCBSettings,
  outerCopperWeight: number,
) =>
  settings.instant_quote_options.outer_copper_weight.includes(
    outerCopperWeight,
  );
export const substrateThicknessIsInstantQuoteValue = (
  settings: FlashPCBSettings,
  substrateThickness: number,
) =>
  settings.instant_quote_options.substrate_thickness.includes(
    substrateThickness,
  );
export const surfaceFinishIsInstantQuoteValue = (
  settings: FlashPCBSettings,
  surfaceFinish: string,
  leadTimeDays: number,
) =>
  settings.instant_quote_options.surface_finish[leadTimeDays]!.includes(
    surfaceFinish,
  );
export const substrateIsInstantQuoteValue = (
  settings: FlashPCBSettings,
  substrate: string,
) => settings.instant_quote_options.substrate.includes(substrate);

function useUpdatePhysicalBoardDetails() {
  const board = useBoardFromContext().board as Board;
  const dispatch = useAppDispatch();

  const resetForm = useCallback(() => {
    dispatch(physicalDetailsLoaded({ board: board }));
  }, [board, dispatch]);

  const { sendUpdateEvent } = useSendUpdateEvent(resetForm);

  const updatePhysicalDetails = useCallback(
    (update: (prevState: PhysicalDetails) => PhysicalDetails) => {
      if (board.physical_details !== null) {
        const updatedPhysicalDetails = update(board.physical_details);
        dispatch(
          physicalDetailsLoaded({
            board: {
              ...board,
              physical_details: updatedPhysicalDetails,
            },
          }),
        );
        void sendUpdateEvent({
          machine_group: MachineGroup.BOARD,
          model_id: board.id,
          event: BoardTransitionEvent.SHOULD_UPDATE_PHYSICAL_DETAILS,
          update: {
            physical_detail_update: updatedPhysicalDetails,
          },
        });
      }
    },
    [dispatch, board, sendUpdateEvent],
  );

  return { updatePhysicalDetails };
}

interface CopperWeightSelectOption {
  value: number;
  label: string;
}

interface SubstrateThicknessSelectOption {
  value: number;
  label: string;
}

const UNKNOWN = "Unknown";
const CUSTOM_QUOTE_QUALIFIER = " (requires custom quote)";

export function getNumLayers(physicalDetails: PhysicalDetails | null) {
  if (physicalDetails === null) {
    return UNKNOWN;
  }
  return physicalDetails.num_layers;
}

export function getBoundingBoxWidth(physicalDetails: PhysicalDetails | null) {
  if (physicalDetails === null) {
    return UNKNOWN;
  }
  return `${physicalDetails.bounding_box_width.toFixed(2)} ${
    physicalDetails.bounding_box_unit
  }`;
}

export function getBoundingBoxHeight(physicalDetails: PhysicalDetails | null) {
  if (physicalDetails === null) {
    return UNKNOWN;
  }
  return `${physicalDetails.bounding_box_height.toFixed(2)} ${
    physicalDetails.bounding_box_unit
  }`;
}
export const getMinimumHoleDiameterText = (
  physicalDetails: PhysicalDetails | null,
) => {
  if (
    physicalDetails !== null &&
    physicalDetails.min_hole_diameter !== null &&
    physicalDetails.min_hole_diameter_unit !== null
  ) {
    return `${physicalDetails.min_hole_diameter.toLocaleString(undefined, { maximumFractionDigits: 4 })} ${physicalDetails.min_hole_diameter_unit}`;
  } else {
    return UNKNOWN;
  }
};
const PhysicalDetailsForm = () => {
  const flashPCBSettings = useFlashPCBSettings();
  const board = useBoardFromContext().board as Board;
  const shipments = useShipmentsFromContext(board.id) as Shipment[];
  const { updatePhysicalDetails } = useUpdatePhysicalBoardDetails();
  const isAdminOrEngineer = useAppSelector(
    (state) => state.auth.isAdminOrEngineer,
  );

  const isDemoBoard = board.id == DEMO_BOARD_SLUG;
  const quoteIsViewOnly = boardHasBeenPaidFor(board) || isDemoBoard;

  if (
    board.physical_details === null &&
    board.physical_details_state == PhysicalDetailsState.PROCESSED
  ) {
    return null;
  }
  const notProcessed =
    board.physical_details_state != PhysicalDetailsState.PROCESSED ||
    board.physical_details === null;

  const PROJECT_DETAILS_TEXT = [
    {
      label: "Number of layers:",
      node: notProcessed ? (
        <ProcessingPill />
      ) : (
        getNumLayers(board.physical_details)
      ),
    },
    {
      label: "Bounding box width:",
      node: notProcessed ? (
        <ProcessingPill />
      ) : (
        getBoundingBoxWidth(board.physical_details)
      ),
    },
    {
      label: "Bounding box height:",
      node: notProcessed ? (
        <ProcessingPill />
      ) : (
        getBoundingBoxHeight(board.physical_details)
      ),
    },
    {
      label: "Minimum hole diameter:",
      node: notProcessed ? (
        <ProcessingPill />
      ) : (
        getMinimumHoleDiameterText(board.physical_details)
      ),
    },
  ];

  const onChangeOuterCopperWeight = (
    newValue: SingleValue<CopperWeightSelectOption>,
  ) => {
    if (newValue) {
      updatePhysicalDetails((prevState) => ({
        ...(prevState as PhysicalDetails),
        outer_copper_weight: newValue.value,
      }));
    }
  };

  const onChangeInnerCopperWeight = (
    newValue: SingleValue<CopperWeightSelectOption>,
  ) => {
    if (newValue) {
      updatePhysicalDetails((prevState) => ({
        ...(prevState as PhysicalDetails),
        inner_copper_weight: newValue.value,
      }));
    }
  };

  const onChangeSubstrate = (newValue: SingleValue<OptionType>) => {
    if (newValue) {
      updatePhysicalDetails((prevState) => ({
        ...(prevState as PhysicalDetails),
        substrate: newValue.value,
      }));
    }
  };

  const onChangeSubstrateThickness = (
    newValue: SingleValue<SubstrateThicknessSelectOption>,
  ) => {
    if (newValue) {
      updatePhysicalDetails((prevState) => ({
        ...(prevState as PhysicalDetails),
        substrate_thickness: newValue.value,
      }));
    }
  };

  const onChangeSoldermaskColor = (newValue: SingleValue<OptionType>) => {
    if (newValue) {
      updatePhysicalDetails((prevState) => ({
        ...(prevState as PhysicalDetails),
        soldermask_color: newValue.value,
      }));
    }
  };

  const onChangeSurfaceFinish = (newValue: SingleValue<OptionType>) => {
    if (newValue) {
      updatePhysicalDetails((prevState) => ({
        ...(prevState as PhysicalDetails),
        surface_finish: newValue.value,
      }));
    }
  };

  const onChangeSolderType = (newValue: SingleValue<OptionType>) => {
    if (newValue) {
      updatePhysicalDetails((prevState) => ({
        ...(prevState as PhysicalDetails),
        solder_type: newValue.value,
      }));
    }
  };

  if (flashPCBSettings === null) {
    return <LoadingIcon />;
  }

  const copperWeightToString = (copperWeight: number, layer: Layer) => {
    const copperWeightString = `${
      copperWeight.toFixed(2) +
      " " +
      (board.physical_details?.copper_weight_unit || "")
    }`;
    if (layer == Layer.INNER) {
      return innerCopperWeightIsInstantQuoteValue(
        flashPCBSettings,
        copperWeight,
      )
        ? copperWeightString
        : copperWeightString + CUSTOM_QUOTE_QUALIFIER;
    } else {
      return outerCopperWeightIsInstantQuoteValue(
        flashPCBSettings,
        copperWeight,
      )
        ? copperWeightString
        : copperWeightString + CUSTOM_QUOTE_QUALIFIER;
    }
  };

  const getSubstrateLabel = (substrate: string) => {
    return substrateIsInstantQuoteValue(flashPCBSettings, substrate)
      ? substrate
      : substrate + CUSTOM_QUOTE_QUALIFIER;
  };

  const getSubstrateThicknessLabel = (substrateThickness: number) => {
    return substrateThicknessIsInstantQuoteValue(
      flashPCBSettings,
      substrateThickness,
    )
      ? `${substrateThickness.toFixed(1)} ${board.physical_details?.substrate_thickness_unit || ""}`
      : `${substrateThickness.toFixed(1)} ${board.physical_details?.substrate_thickness_unit || ""} ` +
          CUSTOM_QUOTE_QUALIFIER;
  };

  const getSurfaceFinishLabel = (surfaceFinish: string) => {
    return surfaceFinishIsInstantQuoteValue(
      flashPCBSettings,
      surfaceFinish,
      shipments[0]!.base_lead_time_days,
    )
      ? surfaceFinish
      : surfaceFinish + CUSTOM_QUOTE_QUALIFIER;
  };

  const boardPhysicalDetailOptions = {
    soldermask_color:
      flashPCBSettings.physical_details_options.soldermask_color.map(
        (option) => ({
          label: option,
          value: option,
        }),
      ),
    inner_copper_weight:
      flashPCBSettings.physical_details_options.inner_copper_weight.map(
        (option) => ({
          label: copperWeightToString(option, Layer.INNER),
          value: option,
        }),
      ),
    outer_copper_weight:
      flashPCBSettings.physical_details_options.outer_copper_weight
        .filter((option) => {
          if (option === 3) {
            return isAdminOrEngineer;
          }
          return true;
        })
        .map((option) => ({
          label: copperWeightToString(option, Layer.OUTER),
          value: option,
        })),
    substrate: flashPCBSettings.physical_details_options.substrate.map(
      (option) => ({
        label: getSubstrateLabel(option),
        value: option,
      }),
    ),
    surface_finish:
      flashPCBSettings.physical_details_options.surface_finish.map(
        (option) => ({
          label: getSurfaceFinishLabel(option),
          value: option,
        }),
      ),
    substrate_thickness:
      flashPCBSettings.physical_details_options.substrate_thickness.map(
        (option) => ({
          label: getSubstrateThicknessLabel(option),
          value: option,
        }),
      ),
    solder_type: flashPCBSettings.physical_details_options.solder_type.map(
      (option) => ({
        label: option,
        value: option,
      }),
    ),
  };

  const soldermaskTooltip = (
    <FlashPCBTooltip content="The preview currently shows green soldermask even if you select a different color. However, we will manufacture your board with the color you selected.">
      <InfoIcon />
    </FlashPCBTooltip>
  );

  if (quoteIsViewOnly && board.physical_details !== null) {
    return (
      <>
        {PROJECT_DETAILS_TEXT.map((detail) => (
          <Container key={detail.label}>
            <InputLabel>{detail.label}</InputLabel>
            {detail.node}
          </Container>
        ))}
        <Container>
          <InputLabel>Soldermask color: {soldermaskTooltip}</InputLabel>
          {board.physical_details.soldermask_color}
        </Container>
        <Container>
          <InputLabel>Outer copper weight:</InputLabel>
          {copperWeightToString(
            board.physical_details.outer_copper_weight,
            Layer.OUTER,
          )}
        </Container>
        {board.physical_details.num_layers &&
          board.physical_details.num_layers > 2 && (
            <Container>
              <InputLabel>Inner copper weight:</InputLabel>
              {copperWeightToString(
                board.physical_details.inner_copper_weight,
                Layer.INNER,
              )}
            </Container>
          )}
        <Container>
          <InputLabel>Substrate thickness:</InputLabel>
          {getSubstrateThicknessLabel(
            board.physical_details.substrate_thickness,
          )}
        </Container>
        <Container>
          <InputLabel>Surface finish:</InputLabel>
          {getSurfaceFinishLabel(board.physical_details.surface_finish)}
        </Container>
        <Container>
          <InputLabel>Solder type:</InputLabel>
          {board.physical_details.solder_type}
        </Container>
      </>
    );
  }

  if (board.physical_details === null) {
    return null;
  }

  return (
    <>
      {PROJECT_DETAILS_TEXT.map((detail) => (
        <Container key={detail.label}>
          <InputLabel>{detail.label}</InputLabel>
          {detail.node}
        </Container>
      ))}
      <LineItemDescription>
        *Some selections will require a custom quote
      </LineItemDescription>
      <DetailLine>
        <QuoteOptionContainer>
          <Label>Solder mask color: {soldermaskTooltip}</Label>
          {notProcessed ? (
            <ProcessingPill />
          ) : (
            <FlashPCBSelect
              options={boardPhysicalDetailOptions.soldermask_color}
              value={{
                value: board.physical_details.soldermask_color,
                label: board.physical_details.soldermask_color,
              }}
              onChange={onChangeSoldermaskColor}
            />
          )}
        </QuoteOptionContainer>
      </DetailLine>
      <DetailLine>
        <QuoteOptionContainer>
          <Label>Outer copper weight:</Label>
          {notProcessed ? (
            <ProcessingPill />
          ) : (
            <FlashPCBSelect
              options={boardPhysicalDetailOptions.outer_copper_weight}
              value={{
                value: board.physical_details.outer_copper_weight,
                label: copperWeightToString(
                  board.physical_details.outer_copper_weight,
                  Layer.OUTER,
                ),
              }}
              onChange={onChangeOuterCopperWeight}
            />
          )}
        </QuoteOptionContainer>
      </DetailLine>
      {board.physical_details.num_layers &&
        board.physical_details.num_layers > 2 && (
          <DetailLine>
            <QuoteOptionContainer>
              <Label>Inner copper weight:</Label>
              {notProcessed ? (
                <ProcessingPill />
              ) : (
                <FlashPCBSelect
                  options={boardPhysicalDetailOptions.inner_copper_weight}
                  value={{
                    value: board.physical_details.inner_copper_weight,
                    label: copperWeightToString(
                      board.physical_details.inner_copper_weight,
                      Layer.INNER,
                    ),
                  }}
                  onChange={onChangeInnerCopperWeight}
                />
              )}
            </QuoteOptionContainer>
          </DetailLine>
        )}
      <DetailLine>
        <QuoteOptionContainer>
          <Label>Substrate:</Label>
          {notProcessed ? (
            <ProcessingPill />
          ) : (
            <FlashPCBSelect
              options={boardPhysicalDetailOptions.substrate}
              value={{
                value: board.physical_details.substrate,
                label: getSubstrateLabel(board.physical_details.substrate),
              }}
              onChange={onChangeSubstrate}
            />
          )}
        </QuoteOptionContainer>
      </DetailLine>
      <DetailLine>
        <QuoteOptionContainer>
          <Label>Substrate thickness:</Label>
          {notProcessed ? (
            <ProcessingPill />
          ) : (
            <FlashPCBSelect
              options={
                board.physical_details.num_layers === 4
                  ? boardPhysicalDetailOptions.substrate_thickness.filter(
                      (thickness) =>
                        thickness.value !== 0.4 && thickness.value !== 0.6,
                    )
                  : boardPhysicalDetailOptions.substrate_thickness
              }
              value={{
                value: board.physical_details.substrate_thickness,
                label: getSubstrateThicknessLabel(
                  board.physical_details.substrate_thickness,
                ),
              }}
              onChange={onChangeSubstrateThickness}
            />
          )}
        </QuoteOptionContainer>
      </DetailLine>
      <DetailLine>
        <QuoteOptionContainer>
          <Label>Surface finish:</Label>
          {notProcessed ? (
            <ProcessingPill />
          ) : (
            <FlashPCBSelect
              options={boardPhysicalDetailOptions.surface_finish}
              value={{
                value: board.physical_details.surface_finish,
                label: getSurfaceFinishLabel(
                  board.physical_details.surface_finish,
                ),
              }}
              onChange={onChangeSurfaceFinish}
            />
          )}
        </QuoteOptionContainer>
      </DetailLine>
      <DetailLine>
        <QuoteOptionContainer>
          <Label>Solder type:</Label>
          {notProcessed ? (
            <ProcessingPill />
          ) : (
            <FlashPCBSelect
              options={boardPhysicalDetailOptions.solder_type}
              value={{
                value: board.physical_details.solder_type,
                label: board.physical_details.solder_type,
              }}
              onChange={onChangeSolderType}
            />
          )}
          <LineItemDescription>
            Leaded solder will add a $100 manufacturing fee
          </LineItemDescription>
        </QuoteOptionContainer>
      </DetailLine>
    </>
  );
};

export default PhysicalDetailsForm;
